import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,

  Switch,
  Divider,
  TextField,
  Collapse,
  Grid,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Alert,
  Button,
  Snackbar,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
  Policy as PolicyIcon,
  Gavel as GavelIcon,
  PersonRemove as PersonRemoveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import {
  DarkMode as DarkModeIcon,
  Visibility as VisibilityIcon,
  CardGiftcard as LoyaltyIcon,
  NotificationsActive as SoundIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as RidesIcon,
  Euro as EuroIcon,
  Event as CalendarIcon,
  AllInclusive as InfinityIcon,
  PhoneIphone as PhoneIcon,
  QrCode2 as QrCodeIcon,
  Apple as AppleIcon,
  Shop as PlayStoreIcon,
  LocalTaxi as TaxiIcon,
  NightsStay as NightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExitToApp as AutoLogoutIcon,
  Map as MapIcon,
  AutoAwesome as AutoFollowUpIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import MobileAppQRDialog from './MobileAppQRDialog';
import PageHeader from '../common/PageHeader';
import packageJson from '../../../package.json';
import {
  fetchSettings,
  saveSettings,
  saveSettingImmediately,
  updateLocalSettings,
  loadLocalSettings,
  fetchCloudFunctionSettings,
  saveCloudFunctionSetting
} from '../../redux/slices/settingsSlice';
import {
  fetchFestpreise,
  addFestpreis,
  updateFestpreis,
  deleteFestpreis
} from '../../redux/slices/festpreiseSlice';
import { fetchSectors } from '../../redux/slices/sectorsSlice';
import { selectCanManageGlobalSettings } from '../../redux/slices/authSlice';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

const { version } = packageJson;

/**
 * SettingsPage-Komponente
 *
 * Ermöglicht die Konfiguration verschiedener App-Einstellungen.
 * Globale Einstellungen werden in Firebase gespeichert.
 * Lokale Einstellungen werden in localStorage gespeichert.
 */
const SettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const {
    global: globalSettings,
    cloudFunctions: cloudFunctionSettings,
    local: localSettings,
    isLoading,
    isSaving,
    isInitialized
  } = useSelector((state) => state.settings);

  // Lokaler State für Formular (um Änderungen zu tracken bevor gespeichert wird)
  const [formState, setFormState] = useState({
    // Globale Einstellungen (Treuekarte)
    loyaltyCardActive: false,
    loyaltyCardRidesRequired: 5,
    loyaltyCardDiscountEur: 5,
    loyaltyCardEndDate: '',
    loyaltyCardNoEndDate: true,
    // Mobile App Store Links
    appStoreUrl: '',
    playStoreUrl: '',
    // Tarif-Einstellungen
    tarifGrundpreis: 3.5,
    tarifPreisProKm: 2.5,
    tarifGrossraumzuschlag: 5.0,
    // Nachttarif-Einstellungen
    nachtTarifAktiv: false,
    nachtTarifGrundpreis: 3.5,
    nachtTarifPreisProKm: 2.5,
    nachtTarifWocheStart: '22:00',
    nachtTarifWocheEnd: '06:00',
    nachtTarifWochenendStart: '20:00',
    nachtTarifWochenendEnd: '07:00'
  });

  // Auto-Logout Form State (Minuten-Eingabe)
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(120);

  // Vorbestellungen Cloud Function Form State
  const [convertToOpenMinutes, setConvertToOpenMinutes] = useState(15);
  const [preAcceptedCheckMins, setPreAcceptedCheckMins] = useState(15);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [dispatchMethodConfirm, setDispatchMethodConfirm] = useState(null);

  // Festpreise State
  const festpreiseState = useSelector((state) => state.festpreise);
  const sectors = useSelector((state) => state.sectors?.sectors || []);
  const canManageGlobalSettings = useSelector(selectCanManageGlobalSettings);
  const sofortbestellungAnzeigeMethode =
    globalSettings.sofortbestellungAnzeigeMethode === 'freimeldung'
      ? 'sectors'
      : (globalSettings.sofortbestellungAnzeigeMethode || 'distance');
  const isSectorDispatchActive = sofortbestellungAnzeigeMethode === 'sectors';
  const activeSectorCount = sectors.filter(sector => sector.active !== false).length;
  const [festpreisDialogOpen, setFestpreisDialogOpen] = useState(false);
  const [festpreiseExpanded, setFestpreiseExpanded] = useState(false);
  const [editingFestpreis, setEditingFestpreis] = useState(null);
  const [festpreisForm, setFestpreisForm] = useState({
    start: '', startAdresse: '', ziel: '', zielAdresse: '', preis: '', aktiv: true, festAdresseModus: 'ziel'
  });

  // Lade Einstellungen beim Mount
  useEffect(() => {
    dispatch(fetchSettings());
    dispatch(loadLocalSettings());
    dispatch(fetchFestpreise());
    dispatch(fetchCloudFunctionSettings());
    dispatch(fetchSectors());
  }, [dispatch]);

  useEffect(() => {
    if (canManageGlobalSettings && isInitialized && globalSettings.sofortbestellungAnzeigeMethode === 'freimeldung') {
      dispatch(saveSettingImmediately({ key: 'sofortbestellungAnzeigeMethode', value: 'sectors' }));
    }
  }, [canManageGlobalSettings, dispatch, isInitialized, globalSettings.sofortbestellungAnzeigeMethode]);

  const dispatchMethodLabel = (method) =>
    method === 'sectors' ? 'Nach Sektoren' : 'Entfernungsbasiert';

  const handleDispatchMethodChangeRequest = (nextMethod) => {
    if (nextMethod === sofortbestellungAnzeigeMethode) return;
    setDispatchMethodConfirm({
      previousMethod: sofortbestellungAnzeigeMethode,
      nextMethod
    });
  };

  const handleConfirmDispatchMethodChange = async () => {
    if (!dispatchMethodConfirm) return;
    try {
      await dispatch(saveSettingImmediately({
        key: 'sofortbestellungAnzeigeMethode',
        value: dispatchMethodConfirm.nextMethod
      })).unwrap();
      setSnackbar({
        open: true,
        message: 'Anzeige-Methode wurde gespeichert. Alle Fahrer werden abgemeldet und muessen sich neu anmelden.',
        severity: 'success'
      });
      setDispatchMethodConfirm(null);
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler beim Speichern: ${error}`, severity: 'error' });
    }
  };

  // Synchronisiere Auto-Logout Minuten mit Redux State
  useEffect(() => {
    setAutoLogoutMinutes(cloudFunctionSettings.autoLogoutBackgroundMinutes ?? 120);
  }, [cloudFunctionSettings.autoLogoutBackgroundMinutes]);

  // Synchronisiere Vorbestellungen Cloud Function Werte mit Redux State
  useEffect(() => {
    setConvertToOpenMinutes(cloudFunctionSettings.convertToOpenRideMinutes ?? 15);
  }, [cloudFunctionSettings.convertToOpenRideMinutes]);

  useEffect(() => {
    setPreAcceptedCheckMins(cloudFunctionSettings.preAcceptedCheckMinutes ?? 15);
  }, [cloudFunctionSettings.preAcceptedCheckMinutes]);

  // Synchronisiere Redux-State mit lokalem Formular-State
  useEffect(() => {
    if (isInitialized) {
      setFormState({
        loyaltyCardActive: globalSettings.loyaltyCardActive || false,
        loyaltyCardRidesRequired: globalSettings.loyaltyCardRidesRequired || 5,
        loyaltyCardDiscountEur: globalSettings.loyaltyCardDiscountEur || 5,
        loyaltyCardEndDate: globalSettings.loyaltyCardEndDate || '',
        loyaltyCardNoEndDate: globalSettings.loyaltyCardNoEndDate !== false,
        appStoreUrl: globalSettings.appStoreUrl || '',
        playStoreUrl: globalSettings.playStoreUrl || '',
        tarifGrundpreis: globalSettings.tarifGrundpreis ?? 3.5,
        tarifPreisProKm: globalSettings.tarifPreisProKm ?? 2.5,
        tarifGrossraumzuschlag: globalSettings.tarifGrossraumzuschlag ?? 5.0,
        nachtTarifAktiv: globalSettings.nachtTarifAktiv ?? false,
        nachtTarifGrundpreis: globalSettings.nachtTarifGrundpreis ?? 3.5,
        nachtTarifPreisProKm: globalSettings.nachtTarifPreisProKm ?? 2.5,
        nachtTarifWocheStart: globalSettings.nachtTarifWocheStart ?? '22:00',
        nachtTarifWocheEnd: globalSettings.nachtTarifWocheEnd ?? '06:00',
        nachtTarifWochenendStart: globalSettings.nachtTarifWochenendStart ?? '20:00',
        nachtTarifWochenendEnd: globalSettings.nachtTarifWochenendEnd ?? '07:00'
      });
    }
  }, [isInitialized, globalSettings]);

  // Handler für lokale Einstellungen (werden sofort gespeichert)
  const handleLocalToggle = (setting) => () => {
    dispatch(updateLocalSettings({
      [setting]: !localSettings[setting]
    }));
  };

  // Handler für globale Toggle-Einstellungen (Treuekarte - nur im Formular)
  const handleGlobalToggle = (setting) => () => {
    setFormState(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  // Handler für sofortiges Speichern einer globalen Einstellung
  const handleImmediateToggle = (setting, currentValue) => async () => {
    try {
      await dispatch(saveSettingImmediately({ key: setting, value: !currentValue })).unwrap();
      setSnackbar({
        open: true,
        message: 'Einstellung wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  const handleImmediateNumberSetting = (setting, min, max) => async (event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;

    try {
      await dispatch(saveSettingImmediately({ key: setting, value })).unwrap();
      setSnackbar({
        open: true,
        message: 'Einstellung wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Handler für Änderungen
  const handleChange = (setting) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Handler für Zahlen-Eingaben
  const handleNumberChange = (setting, min, max) => (event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Handler für Dezimal-Eingaben (Tarif-Werte)
  const handleDecimalChange = (setting, min, max) => (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;
    // Auf 2 Dezimalstellen runden
    value = Math.round(value * 100) / 100;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Berechnet die Tarif-Vorschau für eine Beispielstrecke
  const calculateFarePreview = (distanceKm, withZuschlag = false) => {
    let fare = formState.tarifGrundpreis + distanceKm * formState.tarifPreisProKm;
    if (withZuschlag) fare += formState.tarifGrossraumzuschlag;
    return (Math.round(fare * 10) / 10).toFixed(2);
  };

  // Berechnet die Nachttarif-Vorschau für eine Beispielstrecke
  const calculateNightFarePreview = (distanceKm, withZuschlag = false) => {
    let fare = formState.nachtTarifGrundpreis + distanceKm * formState.nachtTarifPreisProKm;
    if (withZuschlag) fare += formState.tarifGrossraumzuschlag;
    return (Math.round(fare * 10) / 10).toFixed(2);
  };

  // Speichern der Nachttarif-Einstellungen
  const handleSaveNachtTarif = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'nachtTarifAktiv', value: formState.nachtTarifAktiv })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifGrundpreis', value: formState.nachtTarifGrundpreis })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifPreisProKm', value: formState.nachtTarifPreisProKm })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWocheStart', value: formState.nachtTarifWocheStart })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWocheEnd', value: formState.nachtTarifWocheEnd })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWochenendStart', value: formState.nachtTarifWochenendStart })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWochenendEnd', value: formState.nachtTarifWochenendEnd })).unwrap();
      setSnackbar({
        open: true,
        message: 'Nachttarif-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der Tarif-Einstellungen
  const handleSaveTarif = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'tarifGrundpreis', value: formState.tarifGrundpreis })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'tarifPreisProKm', value: formState.tarifPreisProKm })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'tarifGrossraumzuschlag', value: formState.tarifGrossraumzuschlag })).unwrap();
      setSnackbar({
        open: true,
        message: 'Tarif-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der globalen Einstellungen
  const handleSave = async () => {
    try {
      await dispatch(saveSettings({
        ...formState,
        showPreordersToAllDrivers: globalSettings.showPreordersToAllDrivers || false,
        autoAcceptPreBookings: globalSettings.autoAcceptPreBookings || false
      })).unwrap();
      setSnackbar({
        open: true,
        message: 'Treuekarten-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der Mobile App Links
  const handleSaveMobileAppLinks = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'appStoreUrl', value: formState.appStoreUrl })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'playStoreUrl', value: formState.playStoreUrl })).unwrap();
      setSnackbar({
        open: true,
        message: 'App-Links wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Festpreis Dialog Handler
  const handleOpenFestpreisDialog = (festpreis = null) => {
    if (festpreis) {
      setEditingFestpreis(festpreis);
      setFestpreisForm({
        start: festpreis.start || '',
        startAdresse: festpreis.startAdresse || '',
        ziel: festpreis.ziel || '',
        zielAdresse: festpreis.zielAdresse || '',
        preis: festpreis.preis ?? '',
        aktiv: festpreis.aktiv !== false,
        festAdresseModus: festpreis.festAdresseModus || 'ziel'
      });
    } else {
      setEditingFestpreis(null);
      setFestpreisForm({ start: '', startAdresse: '', ziel: '', zielAdresse: '', preis: '', aktiv: true, festAdresseModus: 'ziel' });
    }
    setFestpreisDialogOpen(true);
  };

  const handleSaveFestpreis = async () => {
    const data = {
      start: festpreisForm.start,
      ziel: festpreisForm.ziel,
      preis: parseFloat(festpreisForm.preis),
      aktiv: festpreisForm.aktiv,
      festAdresseModus: festpreisForm.festAdresseModus,
      ...(festpreisForm.startAdresse && { startAdresse: festpreisForm.startAdresse }),
      ...(festpreisForm.zielAdresse && { zielAdresse: festpreisForm.zielAdresse })
    };
    try {
      if (editingFestpreis) {
        await dispatch(updateFestpreis({ id: editingFestpreis.id, data })).unwrap();
      } else {
        await dispatch(addFestpreis(data)).unwrap();
      }
      setFestpreisDialogOpen(false);
      setSnackbar({ open: true, message: 'Festpreis wurde gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  const handleDeleteFestpreis = async (id) => {
    try {
      await dispatch(deleteFestpreis(id)).unwrap();
      setSnackbar({ open: true, message: 'Festpreis wurde gelöscht', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  // Speichert Auto-Logout Minuten in appSettings/cloudfunctions
  const handleSaveAutoLogoutMinutes = async () => {
    try {
      await dispatch(saveCloudFunctionSetting({
        key: 'autoLogoutBackgroundMinutes',
        value: autoLogoutMinutes
      })).unwrap();
      setSnackbar({ open: true, message: 'Abmeldezeit gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  // Speichert convertToOpenRideMinutes in appSettings/cloudfunctions
  const handleSaveConvertMinutes = async () => {
    try {
      await dispatch(saveCloudFunctionSetting({
        key: 'convertToOpenRideMinutes',
        value: convertToOpenMinutes
      })).unwrap();
      setSnackbar({ open: true, message: 'Umwandlungszeit gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  // Speichert preAcceptedCheckMinutes in appSettings/cloudfunctions
  const handleSavePreAcceptedMinutes = async () => {
    try {
      await dispatch(saveCloudFunctionSetting({
        key: 'preAcceptedCheckMinutes',
        value: preAcceptedCheckMins
      })).unwrap();
      setSnackbar({ open: true, message: 'Prüfzeit gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Formatiere das minimale Datum (heute)
  const today = new Date().toISOString().split('T')[0];

  const breadcrumbs = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Einstellungen' }
  ];

  // Zeige Ladeindikator
  if (isLoading && !isInitialized) {
    return (
      <Container maxWidth="md">
        <PageHeader
          title="Einstellungen"
          breadcrumbs={breadcrumbs}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <PageHeader
        title="Einstellungen"
        breadcrumbs={breadcrumbs}
        description="Konfigurieren Sie die App-Einstellungen"
      />

      {!canManageGlobalSettings && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Diese Einstellungen werden zentral von Taxiunion Pinneberg verwaltet. Ihr Unternehmer-Konto kann die Werte einsehen, aber nicht aendern.
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 0 }}>
        {/* Abschnitt: Darstellung */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Darstellung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Lokale Einstellung (nur dieses Gerät)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={localSettings.darkMode}
                onChange={handleLocalToggle('darkMode')}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <DarkModeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Nachtmodus"
              secondary="Dunkles Farbschema für die Anwendung aktivieren"
            />
          </ListItem>
        </List>

        <Divider />

        <Box sx={!canManageGlobalSettings ? { opacity: 0.62, pointerEvents: 'none' } : undefined}>

        {/* Abschnitt: Vorbestellungen */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Vorbestellungen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (gilt für alle Fahrer)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.showPreordersToAllDrivers || false}
                onChange={handleImmediateToggle('showPreordersToAllDrivers', globalSettings.showPreordersToAllDrivers || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <VisibilityIcon />
            </ListItemIcon>
            <ListItemText
              primary="Vorbestellungen allen Fahrern anzeigen"
              secondary="Alle Fahrer können Vorbestellungen sehen und annehmen"
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.autoAcceptPreBookings || false}
                onChange={handleImmediateToggle('autoAcceptPreBookings', globalSettings.autoAcceptPreBookings || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <ScheduleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Vorbestellungen automatisch bestätigen"
              secondary="Kunden erhalten direkt eine Bestätigung. Die Fahrt bleibt bis zur Fahrerannahme als offene Vorbestellung sichtbar."
            />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Sofortbestellungen */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Sofortbestellungen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Anzeige-Methode für alle Fahrer)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
              Wie sollen neue Sofortbestellungen den Fahrern angezeigt werden?
            </FormLabel>
            <RadioGroup
              value={sofortbestellungAnzeigeMethode}
              onChange={(e) => handleDispatchMethodChangeRequest(e.target.value)}
            >
              <FormControlLabel
                value="distance"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Entfernungsbasiert (Standard)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nähere Fahrer sehen die Fahrt früher (0–5 Sek. Verzögerung je nach Entfernung)
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                value="sectors"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Nach Sektoren</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fahrer im Abholsektor werden nach Sektor-Warteschlange exklusiv angeboten, danach greift der Entfernungs-Fallback
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 1 }}
              />
            </RadioGroup>
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {activeSectorCount} aktive Sektoren
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sektoren als präzise Polygone vorbereiten, zeichnen, bearbeiten und verwalten.
                  </Typography>
                </Box>
                <Button
                  variant={isSectorDispatchActive ? 'contained' : 'outlined'}
                  startIcon={<MapIcon />}
                  onClick={() => navigate('/einstellungen/sektoren')}
                >
                  Sektoren verwalten
                </Button>
              </Stack>

              {!isSectorDispatchActive && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Die Sektoren können vorbereitet werden, ohne die aktuelle entfernungsbasierte Disposition zu verändern.
                  Wirksam werden sie erst nach dem Umstellen auf „Nach Sektoren“.
                </Alert>
              )}

              {activeSectorCount === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Es sind noch keine aktiven Sektoren angelegt. Die spätere Sektor-Disposition fällt dann auf Entfernung zurück.
                </Alert>
              )}

              {isSectorDispatchActive && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <List disablePadding>
                    <ListItem
                      disableGutters
                      secondaryAction={
                        <Switch
                          edge="end"
                          checked={globalSettings.sectorAutoFollowUpEnabled === true}
                          onChange={handleImmediateToggle(
                            'sectorAutoFollowUpEnabled',
                            globalSettings.sectorAutoFollowUpEnabled === true
                          )}
                          color="primary"
                        />
                      }
                    >
                      <ListItemIcon>
                        <AutoFollowUpIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Automatische Anschlusstouren anbieten"
                        secondary="Wenn im Abholsektor kein freies Taxi verfügbar ist und auch kein naher Fallback-Fahrer passt, prüft das System belegte Fahrer, die voraussichtlich bald in diesem Sektor frei werden. Der Fahrer muss das Angebot aktiv als Anschlusstour annehmen."
                      />
                    </ListItem>
                  </List>

                  {globalSettings.sectorAutoFollowUpEnabled === true && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Die normale Sektor-Warteschlange und der Nahbereich-Fallback haben Vorrang.
                        Anschlussangebote werden nur als letzter automatischer Schritt erzeugt.
                      </Alert>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Max. Freiwerdezeit"
                            value={globalSettings.sectorAutoFollowUpMaxEtaMinutes ?? 10}
                            onChange={handleImmediateNumberSetting('sectorAutoFollowUpMaxEtaMinutes', 1, 60)}
                            InputProps={{ endAdornment: <InputAdornment position="end">Min.</InputAdornment> }}
                            helperText="Nur Fahrer, die bald frei werden"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Min. Freiwerdezeit"
                            value={globalSettings.sectorAutoFollowUpMinEtaMinutes ?? 1}
                            onChange={handleImmediateNumberSetting('sectorAutoFollowUpMinEtaMinutes', 0, 30)}
                            InputProps={{ endAdornment: <InputAdornment position="end">Min.</InputAdornment> }}
                            helperText="Schützt vor Sofort-Doppelangeboten"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Angebotsdauer"
                            value={globalSettings.sectorAutoFollowUpOfferSeconds ?? 30}
                            onChange={handleImmediateNumberSetting('sectorAutoFollowUpOfferSeconds', 10, 180)}
                            InputProps={{ endAdornment: <InputAdornment position="end">Sek.</InputAdornment> }}
                            helperText="Zeit zum Annehmen"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Pickup-Radius"
                            value={globalSettings.sectorAutoFollowUpPickupRadiusMeters ?? 700}
                            onChange={handleImmediateNumberSetting('sectorAutoFollowUpPickupRadiusMeters', 100, 5000)}
                            InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                            helperText="Freiwerdepunkt nahe Abholung"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Nahbereich-Fallback"
                            value={globalSettings.sectorFallbackNearbyRadiusMeters ?? 1500}
                            onChange={handleImmediateNumberSetting('sectorFallbackNearbyRadiusMeters', 100, 10000)}
                            InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                            helperText="Freier Fahrer in diesem Radius gewinnt"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Max. wartende Anschlusstouren"
                            value={globalSettings.sectorAutoFollowUpMaxPendingPerDriver ?? 1}
                            onChange={handleImmediateNumberSetting('sectorAutoFollowUpMaxPendingPerDriver', 1, 5)}
                            helperText="Pro Fahrer"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </FormControl>
        </Box>

        <Divider />

        {/* Abschnitt: Tarif */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Tarif
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Fahrpreisberechnung für alle Fahrten)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Formel:</strong> Grundpreis + Distanz &times; Preis/km (+ Großraumzuschlag bei Kombi/Spezialfahrzeug)
            <br />
            <strong>Standard 5 km:</strong> {calculateFarePreview(5)} &euro; &nbsp;|&nbsp;
            <strong>10 km:</strong> {calculateFarePreview(10)} &euro;
            <br />
            <strong>Großraum 5 km:</strong> {calculateFarePreview(5, true)} &euro; &nbsp;|&nbsp;
            <strong>10 km:</strong> {calculateFarePreview(10, true)} &euro;
            {formState.nachtTarifAktiv && (
              <>
                <br />
                <br />
                <strong>Nachttarif 5 km:</strong> {calculateNightFarePreview(5)} &euro; &nbsp;|&nbsp;
                <strong>10 km:</strong> {calculateNightFarePreview(10)} &euro;
                <br />
                <strong>Nachttarif Großraum 5 km:</strong> {calculateNightFarePreview(5, true)} &euro; &nbsp;|&nbsp;
                <strong>10 km:</strong> {calculateNightFarePreview(10, true)} &euro;
              </>
            )}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Grundpreis (Flaggenfall)"
                type="number"
                value={formState.tarifGrundpreis}
                onChange={handleDecimalChange('tarifGrundpreis', 0, 50)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 50, step: 0.1 }
                }}
                helperText="Startpreis beim Einsteigen (0-50 &euro;)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Preis pro Kilometer"
                type="number"
                value={formState.tarifPreisProKm}
                onChange={handleDecimalChange('tarifPreisProKm', 0.1, 20)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RidesIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;/km</InputAdornment>
                  ),
                  inputProps: { min: 0.1, max: 20, step: 0.1 }
                }}
                helperText="Kilometerpreis (0,10-20 &euro;)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Großraumzuschlag"
                type="number"
                value={formState.tarifGrossraumzuschlag}
                onChange={handleDecimalChange('tarifGrossraumzuschlag', 0, 100)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 100, step: 0.5 }
                }}
                helperText="Zuschlag für Kombi und Spezialfahrzeug (0-100 &euro;)"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveTarif}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Tarif speichern'}
            </Button>
          </Box>
        </Box>

        {/* Abschnitt: Nachttarif */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <NightIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Nachttarif
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Separate Preise für Nachtfahrten (Mo-Fr und Sa-So konfigurierbar)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={formState.nachtTarifAktiv}
                onChange={handleGlobalToggle('nachtTarifAktiv')}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <NightIcon />
            </ListItemIcon>
            <ListItemText
              primary="Nachttarif aktivieren"
              secondary="Separate Preise außerhalb der regulären Geschäftszeiten"
            />
          </ListItem>
        </List>

        <Collapse in={formState.nachtTarifAktiv} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Vergleich (5 km):</strong> Tag {calculateFarePreview(5)} &euro; &rarr; Nacht {calculateNightFarePreview(5)} &euro;
              &nbsp;|&nbsp;
              <strong>10 km:</strong> Tag {calculateFarePreview(10)} &euro; &rarr; Nacht {calculateNightFarePreview(10)} &euro;
              <br />
              <strong>Großraum (5 km):</strong> Tag {calculateFarePreview(5, true)} &euro; &rarr; Nacht {calculateNightFarePreview(5, true)} &euro;
              &nbsp;|&nbsp;
              <strong>10 km:</strong> Tag {calculateFarePreview(10, true)} &euro; &rarr; Nacht {calculateNightFarePreview(10, true)} &euro;
              <br /><br />
              <strong>Großraumzuschlag</strong> gilt Tag und Nacht gleich.
            </Alert>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Nacht-Preise
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nacht-Grundpreis (Flaggenfall)"
                  type="number"
                  value={formState.nachtTarifGrundpreis}
                  onChange={handleDecimalChange('nachtTarifGrundpreis', 0, 50)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">&euro;</InputAdornment>
                    ),
                    inputProps: { min: 0, max: 50, step: 0.1 }
                  }}
                  helperText="Startpreis nachts beim Einsteigen"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nacht-Preis pro Kilometer"
                  type="number"
                  value={formState.nachtTarifPreisProKm}
                  onChange={handleDecimalChange('nachtTarifPreisProKm', 0.1, 20)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RidesIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">&euro;/km</InputAdornment>
                    ),
                    inputProps: { min: 0.1, max: 20, step: 0.1 }
                  }}
                  helperText="Kilometerpreis nachts"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Zeitfenster: Wochentage (Mo–Fr)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif beginnt (Mo-Fr)"
                  type="time"
                  value={formState.nachtTarifWocheStart}
                  onChange={handleChange('nachtTarifWocheStart')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 22:00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif endet (Mo-Fr)"
                  type="time"
                  value={formState.nachtTarifWocheEnd}
                  onChange={handleChange('nachtTarifWocheEnd')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 06:00 (nächster Tag)"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
              Zeitfenster: Wochenende (Sa–So)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif beginnt (Sa-So)"
                  type="time"
                  value={formState.nachtTarifWochenendStart}
                  onChange={handleChange('nachtTarifWochenendStart')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 20:00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif endet (Sa-So)"
                  type="time"
                  value={formState.nachtTarifWochenendEnd}
                  onChange={handleChange('nachtTarifWochenendEnd')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 07:00 (nächster Tag)"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveNachtTarif}
                disabled={isSaving}
              >
                {isSaving ? 'Wird gespeichert...' : 'Nachttarif speichern'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Abschnitt: Festpreise */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <ListAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Festpreisliste
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vordefinierte Festpreise für Strecken, die Kunden in der App zur Auswahl angezeigt werden
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>So funktioniert das Festpreis-System:</strong>
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                <li>Aktive Einträge werden dem Kunden in der App als auswählbare Strecken angezeigt.</li>
                <li>Einträge sind <strong>richtungsabhängig</strong> — für Hin- und Rückfahrt separate Einträge anlegen.</li>
                <li>Der <strong>Name</strong> (z.B. „Flughafen") wird dem Kunden als Chip angezeigt, die <strong>Adresse</strong> dient dem PLZ-Abgleich: Die App prüft, ob das vom Kunden eingegebene Ziel zur hinterlegten Adresse passt.</li>
                <li><strong>Adresse fixieren:</strong> Pro Eintrag kann entweder die <strong>Startadresse</strong> oder die <strong>Zieladresse</strong> fixiert werden. Das fixierte Feld wird in der Kunden-App automatisch vorausgefüllt und ausgegraut — der Kunde kann nur das andere Feld frei eingeben.</li>
                <li>Der <strong>Großraumzuschlag</strong> (Kombi/Spezialfahrzeug) wird automatisch auf den Festpreis addiert.</li>
                <li>Der <strong>Nachttarif</strong> hat keinen Einfluss auf Festpreise.</li>
                <li>Werden alle Einträge deaktiviert oder gelöscht, verschwindet die Festpreis-Auswahl automatisch aus der Kunden-App.</li>
              </Box>
            </Alert>

            {festpreiseState.items.slice(0, 2).map((fp) => (
              <Card key={fp.id} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {fp.start} → {fp.ziel}: {Number(fp.preis).toFixed(2)} €
                    </Typography>
                    {(fp.startAdresse || fp.zielAdresse) && (
                      <Typography variant="caption" color="text.secondary">
                        {fp.startAdresse && `Von: ${fp.startAdresse}`}
                        {fp.startAdresse && fp.zielAdresse && ' | '}
                        {fp.zielAdresse && `Nach: ${fp.zielAdresse}`}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={fp.aktiv ? 'Aktiv' : 'Inaktiv'}
                    color={fp.aktiv ? 'success' : 'default'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <IconButton size="small" onClick={() => handleOpenFestpreisDialog(fp)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteFestpreis(fp.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}

            {festpreiseState.items.length > 2 && (
              <>
                <Collapse in={festpreiseExpanded}>
                  {festpreiseState.items.slice(2).map((fp) => (
                    <Card key={fp.id} variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {fp.start} → {fp.ziel}: {Number(fp.preis).toFixed(2)} €
                          </Typography>
                          {(fp.startAdresse || fp.zielAdresse) && (
                            <Typography variant="caption" color="text.secondary">
                              {fp.startAdresse && `Von: ${fp.startAdresse}`}
                              {fp.startAdresse && fp.zielAdresse && ' | '}
                              {fp.zielAdresse && `Nach: ${fp.zielAdresse}`}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={fp.aktiv ? 'Aktiv' : 'Inaktiv'}
                          color={fp.aktiv ? 'success' : 'default'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <IconButton size="small" onClick={() => handleOpenFestpreisDialog(fp)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteFestpreis(fp.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardContent>
                    </Card>
                  ))}
                </Collapse>
                <Button
                  size="small"
                  onClick={() => setFestpreiseExpanded(!festpreiseExpanded)}
                  startIcon={festpreiseExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mb: 1 }}
                >
                  {festpreiseExpanded
                    ? 'Weniger anzeigen'
                    : `${festpreiseState.items.length - 2} weitere anzeigen`}
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFestpreisDialog()}
              sx={{ mt: 1 }}
            >
              Festpreis hinzufügen
            </Button>
        </Box>

        <Divider />

        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.showFareToCustomer || false}
                onChange={handleImmediateToggle('showFareToCustomer', globalSettings.showFareToCustomer || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <EuroIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrpreis-Schätzung für Kunden anzeigen"
              secondary="Kunden sehen den geschätzten Fahrpreis beim Bestellen"
            />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Kundenbindung / Treuekarte */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Kundenbindung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (gilt für alle Kunden)
          </Typography>
        </Box>
        <List disablePadding>
          {/* Treuekarte aktiv Switch */}
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={formState.loyaltyCardActive}
                onChange={handleGlobalToggle('loyaltyCardActive')}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <LoyaltyIcon />
            </ListItemIcon>
            <ListItemText
              primary="Treuekarte aktiv"
              secondary="Treuekarten-System für Stammkunden aktivieren"
            />
          </ListItem>
        </List>

        {/* Treuekarten-Einstellungen - immer sichtbar */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Alert
            severity={formState.loyaltyCardActive ? "success" : "info"}
            sx={{ mb: 3 }}
          >
            {formState.loyaltyCardActive ? (
              <>
                <strong>Aktiv:</strong> Kunden erhalten für jede {formState.loyaltyCardRidesRequired}. abgeschlossene Fahrt
                einen Rabatt von {formState.loyaltyCardDiscountEur}€ auf die nächste Fahrt.
                {globalSettings.loyaltyCardStartDate && (
                  <> Aktiv seit: {new Date(globalSettings.loyaltyCardStartDate).toLocaleDateString('de-DE')}.</>
                )}
                {!formState.loyaltyCardNoEndDate && formState.loyaltyCardEndDate && (
                  <> Gültig bis: {new Date(formState.loyaltyCardEndDate).toLocaleDateString('de-DE')}.</>
                )}
              </>
            ) : (
              <>
                <strong>Inaktiv:</strong> Das Treuekarten-System ist derzeit deaktiviert.
                {globalSettings.loyaltyCardStartDate && (
                  <> Zuletzt aktiv seit: {new Date(globalSettings.loyaltyCardStartDate).toLocaleDateString('de-DE')}.</>
                )}
                {' '}Bei erneuter Aktivierung wird ein neues Startdatum gesetzt und die Zählung beginnt von vorn.
              </>
            )}
          </Alert>

          <Grid container spacing={3}>
            {/* Anzahl Fahrten für Rabatt */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fahrten für Rabatt"
                type="number"
                value={formState.loyaltyCardRidesRequired}
                onChange={handleNumberChange('loyaltyCardRidesRequired', 2, 20)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RidesIcon color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 2, max: 20 }
                }}
                helperText="Anzahl Fahrten bis zum Rabatt (2-20)"
              />
            </Grid>

            {/* Rabatthöhe in Euro */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rabatthöhe"
                type="number"
                value={formState.loyaltyCardDiscountEur}
                onChange={handleNumberChange('loyaltyCardDiscountEur', 1, 50)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                  inputProps: { min: 1, max: 50 }
                }}
                helperText="Rabatt pro Einlösung (1-50€)"
              />
            </Grid>

            {/* Enddatum */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Gültigkeitszeitraum
              </Typography>
            </Grid>

            {/* Checkbox für unbestimmte Zeit */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.loyaltyCardNoEndDate}
                    onChange={handleChange('loyaltyCardNoEndDate')}
                    color="primary"
                    icon={<CalendarIcon />}
                    checkedIcon={<InfinityIcon />}
                  />
                }
                label="Für unbestimmte Zeit aktivieren (kein Enddatum)"
              />
            </Grid>

            {/* Enddatum Kalender - nur sichtbar wenn nicht "unbestimmte Zeit" */}
            <Collapse in={!formState.loyaltyCardNoEndDate} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
              <Grid item xs={12} sm={6} sx={{ pl: 3, pt: 2 }}>
                <TextField
                  fullWidth
                  label="Enddatum der Aktion"
                  type="date"
                  value={formState.loyaltyCardEndDate}
                  onChange={handleChange('loyaltyCardEndDate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                    inputProps: { min: today }
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                  helperText="Die Treuekarten-Aktion endet an diesem Datum"
                />
              </Grid>
            </Collapse>

          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Treuekarte speichern'}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Abschnitt: Mobile App */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Mobile App
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (App-Store-Links für Kunden)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="iOS App Store URL"
                value={formState.appStoreUrl}
                onChange={handleChange('appStoreUrl')}
                placeholder="https://apps.apple.com/app/..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AppleIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Link zur App im Apple App Store"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Play Store URL"
                value={formState.playStoreUrl}
                onChange={handleChange('playStoreUrl')}
                placeholder="https://play.google.com/store/apps/..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlayStoreIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Link zur App im Google Play Store"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={() => setQrDialogOpen(true)}
                  disabled={!formState.appStoreUrl && !formState.playStoreUrl}
                >
                  QR-Code anzeigen
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveMobileAppLinks}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'App-Links speichern'}
            </Button>
          </Box>
        </Box>

        {/* QR-Code Dialog */}
        <MobileAppQRDialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          url={`${window.location.origin}/mobile-app`}
        />

        <Divider />

        {/* Abschnitt: Mobile App Rechtstexte */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Mobile App Rechtstexte
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Öffentlich zugängliche Seiten für die mobile App (z.B. für App Store Einträge)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            component="a"
            href="/mobile/datenschutz"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <PolicyIcon />
            </ListItemIcon>
            <ListItemText
              primary="Datenschutzerklärung (Mobile App)"
              secondary={`${window.location.origin}/mobile/datenschutz`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
          <Divider component="li" />
          <ListItem
            component="a"
            href="/mobile/impressum"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <GavelIcon />
            </ListItemIcon>
            <ListItemText
              primary="Impressum (Mobile App)"
              secondary={`${window.location.origin}/mobile/impressum`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
          <Divider component="li" />
          <ListItem
            component="a"
            href="/mobile/konto-loeschen"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <PersonRemoveIcon />
            </ListItemIcon>
            <ListItemText
              primary="Kontolöschung (Google Play Pflicht)"
              secondary={`${window.location.origin}/mobile/konto-loeschen`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Benachrichtigungen */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Ton-Benachrichtigungen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Lokale Einstellung (nur dieses Gerät)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={localSettings.soundOnInstantOrder}
                onChange={handleLocalToggle('soundOnInstantOrder')}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <SoundIcon />
            </ListItemIcon>
            <ListItemText
              primary="Ton bei Sofortbestellung"
              secondary="Akustisches Signal bei neuen Sofortbestellungen"
            />
          </ListItem>
          <Divider component="li" />
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={localSettings.soundOnPreorder}
                onChange={handleLocalToggle('soundOnPreorder')}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <ScheduleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Ton bei Vorbestellung"
              secondary="Akustisches Signal bei neuen Vorbestellungen"
            />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Automatische Abmeldung */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Automatische Abmeldung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Cloud Function – gilt für alle Fahrer)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={cloudFunctionSettings.autoLogoutInactiveDriversEnabled ?? true}
                onChange={async () => {
                  const newValue = !(cloudFunctionSettings.autoLogoutInactiveDriversEnabled ?? true);
                  try {
                    await dispatch(saveCloudFunctionSetting({
                      key: 'autoLogoutInactiveDriversEnabled',
                      value: newValue
                    })).unwrap();
                    setSnackbar({ open: true, message: 'Einstellung gespeichert', severity: 'success' });
                  } catch (error) {
                    setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
                  }
                }}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AutoLogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Automatische Abmeldung aktivieren"
              secondary="Fahrer werden nach längerer Inaktivität im Hintergrund server-seitig abgemeldet"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Zeit bis zur automatischen Abmeldung"
                type="number"
                value={autoLogoutMinutes}
                onChange={(e) => {
                  let v = parseInt(e.target.value, 10);
                  if (isNaN(v) || v < 30) v = 30;
                  if (v > 480) v = 480;
                  setAutoLogoutMinutes(v);
                }}
                disabled={!(cloudFunctionSettings.autoLogoutInactiveDriversEnabled ?? true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">Min</InputAdornment>,
                  inputProps: { min: 30, max: 480, step: 15 }
                }}
                helperText="Hintergrundzeit bis zur Abmeldung (30–480 Min)"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveAutoLogoutMinutes}
              disabled={
                !(cloudFunctionSettings.autoLogoutInactiveDriversEnabled ?? true) ||
                autoLogoutMinutes === (cloudFunctionSettings.autoLogoutBackgroundMinutes ?? 120)
              }
            >
              Speichern
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Abschnitt: Vorbestellungen – Automatische Umwandlung */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Vorbestellungen – Automatische Umwandlung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Cloud Function – gilt für alle Vorbestellungen)
          </Typography>
        </Box>
        <List disablePadding>
          {/* 1. Nicht zugewiesene Vorbestellungen umwandeln */}
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={cloudFunctionSettings.convertScheduledRidesEnabled ?? true}
                onChange={async () => {
                  const newValue = !(cloudFunctionSettings.convertScheduledRidesEnabled ?? true);
                  try {
                    await dispatch(saveCloudFunctionSetting({
                      key: 'convertScheduledRidesEnabled',
                      value: newValue
                    })).unwrap();
                    setSnackbar({ open: true, message: 'Einstellung gespeichert', severity: 'success' });
                  } catch (error) {
                    setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
                  }
                }}
              />
            }
          >
            <ListItemText
              primary="Nicht zugewiesene Vorbestellungen umwandeln"
              secondary="Vorbestellungen ohne Fahrer werden automatisch zur Sofortbestellung umgewandelt"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, pb: 2, pt: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minuten vor Abholzeit"
                type="number"
                value={convertToOpenMinutes}
                onChange={(e) => {
                  let v = parseInt(e.target.value, 10);
                  if (isNaN(v) || v < 5) v = 5;
                  if (v > 120) v = 120;
                  setConvertToOpenMinutes(v);
                }}
                disabled={!(cloudFunctionSettings.convertScheduledRidesEnabled ?? true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">Min.</InputAdornment>
                }}
                helperText="Ab wie vielen Minuten vor der geplanten Abholzeit wird umgewandelt (5–120)"
                size="small"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveConvertMinutes}
              disabled={
                !(cloudFunctionSettings.convertScheduledRidesEnabled ?? true) ||
                convertToOpenMinutes === (cloudFunctionSettings.convertToOpenRideMinutes ?? 15)
              }
            >
              Speichern
            </Button>
          </Box>
        </Box>

        <Divider />

        <List disablePadding>
          {/* 2. Offline-Fahrer bei zugewiesenen Vorbestellungen prüfen */}
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={cloudFunctionSettings.checkPreAcceptedDriverOnlineEnabled ?? true}
                onChange={async () => {
                  const newValue = !(cloudFunctionSettings.checkPreAcceptedDriverOnlineEnabled ?? true);
                  try {
                    await dispatch(saveCloudFunctionSetting({
                      key: 'checkPreAcceptedDriverOnlineEnabled',
                      value: newValue
                    })).unwrap();
                    setSnackbar({ open: true, message: 'Einstellung gespeichert', severity: 'success' });
                  } catch (error) {
                    setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
                  }
                }}
              />
            }
          >
            <ListItemText
              primary="Offline-Fahrer bei zugewiesenen Vorbestellungen prüfen"
              secondary="Zugewiesene Vorbestellungen werden zurückgesetzt wenn der Fahrer vor Abholzeit offline ist"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, pb: 2, pt: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minuten vor Abholzeit"
                type="number"
                value={preAcceptedCheckMins}
                onChange={(e) => {
                  let v = parseInt(e.target.value, 10);
                  if (isNaN(v) || v < 5) v = 5;
                  if (v > 120) v = 120;
                  setPreAcceptedCheckMins(v);
                }}
                disabled={!(cloudFunctionSettings.checkPreAcceptedDriverOnlineEnabled ?? true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">Min.</InputAdornment>
                }}
                helperText="Ab wie vielen Minuten vor der Abholzeit wird der Fahrer-Status geprüft (5–120)"
                size="small"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSavePreAcceptedMinutes}
              disabled={
                !(cloudFunctionSettings.checkPreAcceptedDriverOnlineEnabled ?? true) ||
                preAcceptedCheckMins === (cloudFunctionSettings.preAcceptedCheckMinutes ?? 15)
              }
            >
              Speichern
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Abschnitt: App-Version */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled" display="block">
            NextCab Portal v{version}
          </Typography>
          {globalSettings.currentAppVersion && (
            <Typography variant="caption" color="text.disabled" display="block">
              Mobile App v{globalSettings.currentAppVersion}
              {globalSettings.minAppVersion && ` (min. v${globalSettings.minAppVersion})`}
            </Typography>
          )}
        </Box>
        </Box>
      </Paper>

      {/* Festpreis Dialog */}
      <Dialog open={festpreisDialogOpen} onClose={() => setFestpreisDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFestpreis ? 'Festpreis bearbeiten' : 'Neuer Festpreis'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Die Adressen dienen dem <strong>PLZ-Abgleich</strong> in der Kunden-App. Mit den
            Schaltern <em>„fixieren"</em> legen Sie fest, welches Adressfeld beim Kunden
            vorausgefüllt und <strong>nicht änderbar</strong> ist — das jeweils andere Feld
            gibt der Kunde frei ein.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Start-Name"
              value={festpreisForm.start}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, start: e.target.value }))}
              required
              placeholder="z.B. HBF, Innenstadt"
              fullWidth
            />
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete
                label="Start-Adresse (für PLZ-Abgleich)"
                value={festpreisForm.startAdresse}
                onChange={(e) => setFestpreisForm(prev => ({ ...prev, startAdresse: e.target.value }))}
                onPlaceSelect={(formattedAddress) => {
                  if (formattedAddress) {
                    setFestpreisForm(prev => ({ ...prev, startAdresse: formattedAddress }));
                  }
                }}
              />
            </APIProvider>
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.festAdresseModus === 'start'}
                  onChange={(e) =>
                    setFestpreisForm(prev => ({
                      ...prev,
                      festAdresseModus: e.target.checked ? 'start' : 'ziel'
                    }))
                  }
                />
              }
              label="Startadresse fixieren (Kunde kann diese nicht ändern)"
            />
            <TextField
              label="Ziel-Name"
              value={festpreisForm.ziel}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, ziel: e.target.value }))}
              required
              placeholder="z.B. Flughafen, Nachbarstadt"
              fullWidth
            />
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete
                label="Ziel-Adresse (für PLZ-Abgleich)"
                value={festpreisForm.zielAdresse}
                onChange={(e) => setFestpreisForm(prev => ({ ...prev, zielAdresse: e.target.value }))}
                onPlaceSelect={(formattedAddress) => {
                  if (formattedAddress) {
                    setFestpreisForm(prev => ({ ...prev, zielAdresse: formattedAddress }));
                  }
                }}
              />
            </APIProvider>
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.festAdresseModus === 'ziel'}
                  onChange={(e) =>
                    setFestpreisForm(prev => ({
                      ...prev,
                      festAdresseModus: e.target.checked ? 'ziel' : 'start'
                    }))
                  }
                />
              }
              label="Zieladresse fixieren (Kunde kann diese nicht ändern)"
            />
            <TextField
              label="Preis"
              type="number"
              value={festpreisForm.preis}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, preis: e.target.value }))}
              required
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.5 }
              }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.aktiv}
                  onChange={(e) => setFestpreisForm(prev => ({ ...prev, aktiv: e.target.checked }))}
                />
              }
              label="Aktiv"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFestpreisDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveFestpreis}
            disabled={!festpreisForm.start || !festpreisForm.ziel || !festpreisForm.preis || festpreiseState.isSaving}
          >
            {festpreiseState.isSaving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dispatchMethodConfirm)}
        onClose={() => setDispatchMethodConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Dispositionsmethode umstellen?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Diese Aenderung wirkt sofort auf den laufenden Betrieb.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Wechsel von <strong>{dispatchMethodLabel(dispatchMethodConfirm?.previousMethod)}</strong> auf{' '}
            <strong>{dispatchMethodLabel(dispatchMethodConfirm?.nextMethod)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Alle Fahrer werden automatisch abgemeldet, auch Fahrer mit aktiver Fahrt.
            Zugewiesene Fahrzeuge werden freigegeben. Laufende Sektor-Angebote werden
            abgebrochen bzw. auf die neue Methode zurueckgesetzt. Fahrer muessen sich
            danach neu anmelden und ein Fahrzeug auswaehlen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchMethodConfirm(null)}>Abbrechen</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleConfirmDispatchMethodChange}
          >
            Trotzdem umstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
